import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, Save } from 'lucide-react';
import {
  useIsAdmin,
  useNotificationTemplates,
  useUpdateNotificationTemplates,
} from '../hooks';
import {
  NotificationTemplate,
  NotificationTemplateKey,
  UpdateNotificationTemplateDto,
  AnniversaryNotificationConfig,
  ReviewReminderNotificationConfig,
  MilestoneCheckInNotificationConfig,
  QuarterlyEvalReminderNotificationConfig,
} from '../services/slackService';
import {
  NotificationTemplateEditor,
  findInvalidPlaceholders,
  WorkAnniversaryCard,
  AnniversarySettingsDraft,
  MAX_MILESTONES,
  PerformanceReviewReminderCard,
  ReviewReminderSettingsDraft,
  NOTIFY_ROLE_OPTIONS,
  hasTooManyLeadTimes,
  hasNoRecipients,
  StartDateMilestoneCard,
  MilestoneCheckInSettingsDraft,
  MILESTONE_NOTIFY_ROLE_OPTIONS,
  hasTooManyMilestoneDays,
  hasNoMilestoneRecipients,
  QuarterlyEvalReminderCard,
  QuarterlyEvalReminderSettingsDraft,
  QUARTERLY_EVAL_NOTIFY_ROLE_OPTIONS,
  hasNoQuarterlyEvalRecipients,
} from '../components/admin';
import { PageHeader } from '../components/page-header';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Skeleton } from '../components/ui/skeleton';

interface TemplateDraft {
  body: string;
  enabled: boolean;
}

const toAnniversaryDraft = (
  config: AnniversaryNotificationConfig
): AnniversarySettingsDraft => ({
  channel: config.channel ?? '',
  notificationHour: config.notificationHour,
  sendDmToEmployee: config.sendDmToEmployee,
  tagEmployee: config.tagEmployee,
  tagHere: config.tagHere,
  milestones: [...config.milestones]
    .map((milestone) => ({
      year: milestone.year,
      message: milestone.message ?? '',
      enabled: milestone.enabled,
    }))
    .sort((a, b) => a.year - b.year),
});

const toAnniversaryConfigDto = (
  draft: AnniversarySettingsDraft
): AnniversaryNotificationConfig => ({
  channel: draft.channel.trim() ? draft.channel.trim() : null,
  notificationHour: draft.notificationHour,
  sendDmToEmployee: draft.sendDmToEmployee,
  tagEmployee: draft.tagEmployee,
  tagHere: draft.tagHere,
  milestones: [...draft.milestones]
    .sort((a, b) => a.year - b.year)
    .map((milestone) => ({
      year: milestone.year,
      message: milestone.message,
      enabled: milestone.enabled,
    })),
});

const toReviewReminderDraft = (
  config: ReviewReminderNotificationConfig
): ReviewReminderSettingsDraft => ({
  channel: config.channel ?? '',
  sendToChannel: config.sendToChannel,
  notificationHour: config.notificationHour,
  daysBefore: [...config.daysBefore].sort((a, b) => b - a),
  notifyRoles: NOTIFY_ROLE_OPTIONS.filter((role) => config.notifyRoles.includes(role)),
  sendMode: config.sendMode,
});

const toReviewReminderConfigDto = (
  draft: ReviewReminderSettingsDraft
): ReviewReminderNotificationConfig => ({
  channel: draft.channel.trim() ? draft.channel.trim() : null,
  sendToChannel: draft.sendToChannel,
  notificationHour: draft.notificationHour,
  daysBefore: [...draft.daysBefore].sort((a, b) => b - a),
  notifyRoles: NOTIFY_ROLE_OPTIONS.filter((role) => draft.notifyRoles.includes(role)),
  sendMode: draft.sendMode,
});

const toMilestoneCheckInDraft = (
  config: MilestoneCheckInNotificationConfig
): MilestoneCheckInSettingsDraft => ({
  notificationHour: config.notificationHour,
  milestoneDays: [...config.milestoneDays].sort((a, b) => a - b),
  notifyRoles: MILESTONE_NOTIFY_ROLE_OPTIONS.filter((role) => config.notifyRoles.includes(role)),
  sendMode: config.sendMode,
});

const toMilestoneCheckInConfigDto = (
  draft: MilestoneCheckInSettingsDraft
): MilestoneCheckInNotificationConfig => ({
  notificationHour: draft.notificationHour,
  milestoneDays: [...draft.milestoneDays].sort((a, b) => a - b),
  notifyRoles: MILESTONE_NOTIFY_ROLE_OPTIONS.filter((role) => draft.notifyRoles.includes(role)),
  sendMode: draft.sendMode,
});

const toQuarterlyEvalReminderDraft = (
  config: QuarterlyEvalReminderNotificationConfig
): QuarterlyEvalReminderSettingsDraft => ({
  notificationHour: config.notificationHour,
  notifyRoles: QUARTERLY_EVAL_NOTIFY_ROLE_OPTIONS.filter((role) =>
    config.notifyRoles.includes(role)
  ),
});

const toQuarterlyEvalReminderConfigDto = (
  draft: QuarterlyEvalReminderSettingsDraft
): QuarterlyEvalReminderNotificationConfig => ({
  notificationHour: draft.notificationHour,
  notifyRoles: QUARTERLY_EVAL_NOTIFY_ROLE_OPTIONS.filter((role) =>
    draft.notifyRoles.includes(role)
  ),
});

/**
 * ANNIVERSARY_REVIEW_REMINDER (WORK_ANNIVERSARY) config is only ever attached
 * to its own template key, so this narrows the shared config union safely.
 */
const asReviewReminderConfig = (
  config: NotificationTemplate['config']
): ReviewReminderNotificationConfig | null =>
  config && 'daysBefore' in config ? config : null;

const asAnniversaryConfig = (
  config: NotificationTemplate['config']
): AnniversaryNotificationConfig | null =>
  config && 'milestones' in config ? config : null;

const asMilestoneCheckInConfig = (
  config: NotificationTemplate['config']
): MilestoneCheckInNotificationConfig | null =>
  config && 'milestoneDays' in config ? config : null;

/**
 * The quarterly eval reminder config carries no field unique to it, so it is
 * only ever narrowed from the QUARTERLY_EVAL_REMINDER template's own config.
 */
const asQuarterlyEvalReminderConfig = (
  config: NotificationTemplate['config']
): QuarterlyEvalReminderNotificationConfig | null =>
  config ? (config as QuarterlyEvalReminderNotificationConfig) : null;

const NotificationTemplates: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('admin');

  const { isAdmin, isLoading: adminLoading } = useIsAdmin();
  const { data: templates = [], isLoading, isError } = useNotificationTemplates(
    !adminLoading && isAdmin
  );
  const updateTemplates = useUpdateNotificationTemplates();

  const [drafts, setDrafts] = useState<Record<string, TemplateDraft>>({});
  const [success, setSuccess] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [resettingKey, setResettingKey] = useState<string | null>(null);

  const [anniversaryDraft, setAnniversaryDraft] = useState<AnniversarySettingsDraft | null>(null);
  const [reviewReminderDraft, setReviewReminderDraft] =
    useState<ReviewReminderSettingsDraft | null>(null);
  const [milestoneCheckInDraft, setMilestoneCheckInDraft] =
    useState<MilestoneCheckInSettingsDraft | null>(null);
  const [quarterlyEvalReminderDraft, setQuarterlyEvalReminderDraft] =
    useState<QuarterlyEvalReminderSettingsDraft | null>(null);

  const anniversaryTemplate = useMemo(
    () => templates.find((tpl) => tpl.templateKey === NotificationTemplateKey.WORK_ANNIVERSARY),
    [templates]
  );

  const reviewReminderTemplate = useMemo(
    () =>
      templates.find(
        (tpl) => tpl.templateKey === NotificationTemplateKey.ANNIVERSARY_REVIEW_REMINDER
      ),
    [templates]
  );

  const milestoneCheckInTemplate = useMemo(
    () =>
      templates.find((tpl) => tpl.templateKey === NotificationTemplateKey.START_DATE_MILESTONE),
    [templates]
  );

  const quarterlyEvalReminderTemplate = useMemo(
    () =>
      templates.find(
        (tpl) => tpl.templateKey === NotificationTemplateKey.QUARTERLY_EVAL_REMINDER
      ),
    [templates]
  );

  // Seed the anniversary draft from the WORK_ANNIVERSARY template's config once,
  // preserving in-progress edits afterwards.
  useEffect(() => {
    const config = asAnniversaryConfig(anniversaryTemplate?.config ?? null);
    if (!config) return;
    setAnniversaryDraft((prev) => prev ?? toAnniversaryDraft(config));
  }, [anniversaryTemplate]);

  // Seed the review reminder draft from the ANNIVERSARY_REVIEW_REMINDER
  // template's config once, preserving in-progress edits afterwards.
  useEffect(() => {
    const config = asReviewReminderConfig(reviewReminderTemplate?.config ?? null);
    if (!config) return;
    setReviewReminderDraft((prev) => prev ?? toReviewReminderDraft(config));
  }, [reviewReminderTemplate]);

  // Seed the milestone check-in draft from the START_DATE_MILESTONE template's
  // config once, preserving in-progress edits afterwards.
  useEffect(() => {
    const config = asMilestoneCheckInConfig(milestoneCheckInTemplate?.config ?? null);
    if (!config) return;
    setMilestoneCheckInDraft((prev) => prev ?? toMilestoneCheckInDraft(config));
  }, [milestoneCheckInTemplate]);

  // Seed the quarterly eval reminder draft from the QUARTERLY_EVAL_REMINDER
  // template's config once, preserving in-progress edits afterwards.
  useEffect(() => {
    const config = asQuarterlyEvalReminderConfig(quarterlyEvalReminderTemplate?.config ?? null);
    if (!config) return;
    setQuarterlyEvalReminderDraft((prev) => prev ?? toQuarterlyEvalReminderDraft(config));
  }, [quarterlyEvalReminderTemplate]);

  const updateAnniversaryDraft = (patch: Partial<AnniversarySettingsDraft>) => {
    setSuccess(null);
    setSaveError(null);
    setAnniversaryDraft((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  const updateReviewReminderDraft = (patch: Partial<ReviewReminderSettingsDraft>) => {
    setSuccess(null);
    setSaveError(null);
    setReviewReminderDraft((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  const updateMilestoneCheckInDraft = (patch: Partial<MilestoneCheckInSettingsDraft>) => {
    setSuccess(null);
    setSaveError(null);
    setMilestoneCheckInDraft((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  const updateQuarterlyEvalReminderDraft = (
    patch: Partial<QuarterlyEvalReminderSettingsDraft>
  ) => {
    setSuccess(null);
    setSaveError(null);
    setQuarterlyEvalReminderDraft((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  useEffect(() => {
    if (!adminLoading && !isAdmin) {
      navigate('/dashboard');
    }
  }, [isAdmin, adminLoading, navigate]);

  // Seed drafts for any template not yet tracked, preserving in-progress edits
  // on templates the user is already editing (e.g. after a single reset).
  useEffect(() => {
    if (templates.length === 0) return;
    setDrafts((prev) => {
      const next = { ...prev };
      let changed = false;
      templates.forEach((tpl) => {
        if (!next[tpl.templateKey]) {
          next[tpl.templateKey] = { body: tpl.body, enabled: tpl.enabled };
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [templates]);

  const updateDraft = (templateKey: string, patch: Partial<TemplateDraft>) => {
    setSuccess(null);
    setSaveError(null);
    setDrafts((prev) => ({
      ...prev,
      [templateKey]: { ...prev[templateKey], ...patch },
    }));
  };

  const validationErrors = useMemo(() => {
    const errors: Record<string, string> = {};
    templates.forEach((tpl) => {
      const draft = drafts[tpl.templateKey];
      if (!draft) return;
      if (!draft.body.trim()) {
        errors[tpl.templateKey] = t('notificationTemplates.errors.emptyBody');
        return;
      }
      const invalid = findInvalidPlaceholders(draft.body, tpl.allowedParams);
      if (invalid.length > 0) {
        errors[tpl.templateKey] = t('notificationTemplates.errors.invalidPlaceholder', {
          placeholders: invalid.map((p) => `{${p}}`).join(', '),
        });
      }
    });
    return errors;
  }, [templates, drafts, t]);

  const anniversaryAllowedParams = useMemo(
    () => anniversaryTemplate?.allowedParams ?? [],
    [anniversaryTemplate]
  );

  const reviewReminderAllowedParams = useMemo(
    () => reviewReminderTemplate?.allowedParams ?? [],
    [reviewReminderTemplate]
  );

  const milestoneCheckInAllowedParams = useMemo(
    () => milestoneCheckInTemplate?.allowedParams ?? [],
    [milestoneCheckInTemplate]
  );

  const quarterlyEvalReminderAllowedParams = useMemo(
    () => quarterlyEvalReminderTemplate?.allowedParams ?? [],
    [quarterlyEvalReminderTemplate]
  );

  // Per-milestone validation: every milestone message must be non-blank and may
  // only reference the anniversary template's allowed placeholders.
  const milestoneErrors = useMemo<Record<number, string>>(() => {
    const errors: Record<number, string> = {};
    if (!anniversaryDraft) return errors;
    anniversaryDraft.milestones.forEach((milestone) => {
      if (!milestone.message.trim()) {
        errors[milestone.year] = t('anniversarySettings.errors.emptyMessage');
        return;
      }
      const invalid = findInvalidPlaceholders(milestone.message, anniversaryAllowedParams);
      if (invalid.length > 0) {
        errors[milestone.year] = t('anniversarySettings.errors.invalidPlaceholder', {
          placeholders: invalid.map((p) => `{${p}}`).join(', '),
        });
      }
    });
    return errors;
  }, [anniversaryDraft, anniversaryAllowedParams, t]);

  const changedAnniversaryConfig = useMemo<AnniversaryNotificationConfig | null>(() => {
    const config = asAnniversaryConfig(anniversaryTemplate?.config ?? null);
    if (!config || !anniversaryDraft) return null;
    const current = toAnniversaryConfigDto(toAnniversaryDraft(config));
    const next = toAnniversaryConfigDto(anniversaryDraft);
    return JSON.stringify(current) === JSON.stringify(next) ? null : next;
  }, [anniversaryTemplate, anniversaryDraft]);

  const changedReviewReminderConfig = useMemo<ReviewReminderNotificationConfig | null>(() => {
    const config = asReviewReminderConfig(reviewReminderTemplate?.config ?? null);
    if (!config || !reviewReminderDraft) return null;
    const current = toReviewReminderConfigDto(toReviewReminderDraft(config));
    const next = toReviewReminderConfigDto(reviewReminderDraft);
    return JSON.stringify(current) === JSON.stringify(next) ? null : next;
  }, [reviewReminderTemplate, reviewReminderDraft]);

  const changedMilestoneCheckInConfig = useMemo<MilestoneCheckInNotificationConfig | null>(() => {
    const config = asMilestoneCheckInConfig(milestoneCheckInTemplate?.config ?? null);
    if (!config || !milestoneCheckInDraft) return null;
    const current = toMilestoneCheckInConfigDto(toMilestoneCheckInDraft(config));
    const next = toMilestoneCheckInConfigDto(milestoneCheckInDraft);
    return JSON.stringify(current) === JSON.stringify(next) ? null : next;
  }, [milestoneCheckInTemplate, milestoneCheckInDraft]);

  const changedQuarterlyEvalReminderConfig =
    useMemo<QuarterlyEvalReminderNotificationConfig | null>(() => {
      const config = asQuarterlyEvalReminderConfig(quarterlyEvalReminderTemplate?.config ?? null);
      if (!config || !quarterlyEvalReminderDraft) return null;
      const current = toQuarterlyEvalReminderConfigDto(toQuarterlyEvalReminderDraft(config));
      const next = toQuarterlyEvalReminderConfigDto(quarterlyEvalReminderDraft);
      return JSON.stringify(current) === JSON.stringify(next) ? null : next;
    }, [quarterlyEvalReminderTemplate, quarterlyEvalReminderDraft]);

  const changedTemplates = useMemo<UpdateNotificationTemplateDto[]>(() => {
    const list: UpdateNotificationTemplateDto[] = templates
      .filter((tpl) => {
        const draft = drafts[tpl.templateKey];
        return draft && (draft.body !== tpl.body || draft.enabled !== tpl.enabled);
      })
      .map((tpl) => ({
        templateKey: tpl.templateKey,
        body: drafts[tpl.templateKey].body,
        enabled: drafts[tpl.templateKey].enabled,
      }));

    if (changedAnniversaryConfig && anniversaryTemplate) {
      const existing = list.find(
        (dto) => dto.templateKey === NotificationTemplateKey.WORK_ANNIVERSARY
      );
      if (existing) {
        existing.config = changedAnniversaryConfig;
      } else {
        const draft = drafts[NotificationTemplateKey.WORK_ANNIVERSARY] ?? {
          body: anniversaryTemplate.body,
          enabled: anniversaryTemplate.enabled,
        };
        list.push({
          templateKey: NotificationTemplateKey.WORK_ANNIVERSARY,
          body: draft.body,
          enabled: draft.enabled,
          config: changedAnniversaryConfig,
        });
      }
    }

    if (changedReviewReminderConfig && reviewReminderTemplate) {
      const existing = list.find(
        (dto) => dto.templateKey === NotificationTemplateKey.ANNIVERSARY_REVIEW_REMINDER
      );
      if (existing) {
        existing.config = changedReviewReminderConfig;
      } else {
        const draft = drafts[NotificationTemplateKey.ANNIVERSARY_REVIEW_REMINDER] ?? {
          body: reviewReminderTemplate.body,
          enabled: reviewReminderTemplate.enabled,
        };
        list.push({
          templateKey: NotificationTemplateKey.ANNIVERSARY_REVIEW_REMINDER,
          body: draft.body,
          enabled: draft.enabled,
          config: changedReviewReminderConfig,
        });
      }
    }
    if (changedMilestoneCheckInConfig && milestoneCheckInTemplate) {
      const existing = list.find(
        (dto) => dto.templateKey === NotificationTemplateKey.START_DATE_MILESTONE
      );
      if (existing) {
        existing.config = changedMilestoneCheckInConfig;
      } else {
        const draft = drafts[NotificationTemplateKey.START_DATE_MILESTONE] ?? {
          body: milestoneCheckInTemplate.body,
          enabled: milestoneCheckInTemplate.enabled,
        };
        list.push({
          templateKey: NotificationTemplateKey.START_DATE_MILESTONE,
          body: draft.body,
          enabled: draft.enabled,
          config: changedMilestoneCheckInConfig,
        });
      }
    }
    if (changedQuarterlyEvalReminderConfig && quarterlyEvalReminderTemplate) {
      const existing = list.find(
        (dto) => dto.templateKey === NotificationTemplateKey.QUARTERLY_EVAL_REMINDER
      );
      if (existing) {
        existing.config = changedQuarterlyEvalReminderConfig;
      } else {
        const draft = drafts[NotificationTemplateKey.QUARTERLY_EVAL_REMINDER] ?? {
          body: quarterlyEvalReminderTemplate.body,
          enabled: quarterlyEvalReminderTemplate.enabled,
        };
        list.push({
          templateKey: NotificationTemplateKey.QUARTERLY_EVAL_REMINDER,
          body: draft.body,
          enabled: draft.enabled,
          config: changedQuarterlyEvalReminderConfig,
        });
      }
    }
    return list;
  }, [
    templates,
    drafts,
    changedAnniversaryConfig,
    anniversaryTemplate,
    changedReviewReminderConfig,
    reviewReminderTemplate,
    changedMilestoneCheckInConfig,
    milestoneCheckInTemplate,
    changedQuarterlyEvalReminderConfig,
    quarterlyEvalReminderTemplate,
  ]);

  const tooManyMilestones = (anniversaryDraft?.milestones.length ?? 0) > MAX_MILESTONES;
  const reviewReminderHasErrors =
    !!reviewReminderDraft &&
    (hasTooManyLeadTimes(reviewReminderDraft) || hasNoRecipients(reviewReminderDraft));
  const milestoneCheckInHasErrors =
    !!milestoneCheckInDraft &&
    (hasTooManyMilestoneDays(milestoneCheckInDraft) ||
      hasNoMilestoneRecipients(milestoneCheckInDraft));
  const quarterlyEvalReminderHasErrors =
    !!quarterlyEvalReminderDraft && hasNoQuarterlyEvalRecipients(quarterlyEvalReminderDraft);
  const hasErrors =
    Object.keys(validationErrors).length > 0 ||
    Object.keys(milestoneErrors).length > 0 ||
    tooManyMilestones ||
    reviewReminderHasErrors ||
    milestoneCheckInHasErrors ||
    quarterlyEvalReminderHasErrors;
  const hasChanges = changedTemplates.length > 0;
  const isSaving = updateTemplates.isPending;

  const syncDrafts = (updated: NotificationTemplate[]) => {
    setDrafts((prev) => {
      const next = { ...prev };
      updated.forEach((tpl) => {
        next[tpl.templateKey] = { body: tpl.body, enabled: tpl.enabled };
      });
      return next;
    });
    const updatedAnniversary = updated.find(
      (tpl) => tpl.templateKey === NotificationTemplateKey.WORK_ANNIVERSARY
    );
    const updatedAnniversaryConfig = asAnniversaryConfig(updatedAnniversary?.config ?? null);
    if (updatedAnniversaryConfig) {
      setAnniversaryDraft(toAnniversaryDraft(updatedAnniversaryConfig));
    }
    const updatedReviewReminder = updated.find(
      (tpl) => tpl.templateKey === NotificationTemplateKey.ANNIVERSARY_REVIEW_REMINDER
    );
    const updatedReviewReminderConfig = asReviewReminderConfig(
      updatedReviewReminder?.config ?? null
    );
    if (updatedReviewReminderConfig) {
      setReviewReminderDraft(toReviewReminderDraft(updatedReviewReminderConfig));
    }
    const updatedMilestoneCheckIn = updated.find(
      (tpl) => tpl.templateKey === NotificationTemplateKey.START_DATE_MILESTONE
    );
    const updatedMilestoneCheckInConfig = asMilestoneCheckInConfig(
      updatedMilestoneCheckIn?.config ?? null
    );
    if (updatedMilestoneCheckInConfig) {
      setMilestoneCheckInDraft(toMilestoneCheckInDraft(updatedMilestoneCheckInConfig));
    }
    const updatedQuarterlyEvalReminder = updated.find(
      (tpl) => tpl.templateKey === NotificationTemplateKey.QUARTERLY_EVAL_REMINDER
    );
    const updatedQuarterlyEvalReminderConfig = asQuarterlyEvalReminderConfig(
      updatedQuarterlyEvalReminder?.config ?? null
    );
    if (updatedQuarterlyEvalReminderConfig) {
      setQuarterlyEvalReminderDraft(
        toQuarterlyEvalReminderDraft(updatedQuarterlyEvalReminderConfig)
      );
    }
  };

  const handleSave = async () => {
    setSuccess(null);
    setSaveError(null);
    if (hasErrors || !hasChanges) {
      return;
    }
    try {
      const updated = await updateTemplates.mutateAsync(changedTemplates);
      syncDrafts(updated);
      setSuccess(t('notificationTemplates.success'));
    } catch (error) {
      console.error('Failed to update notification templates:', error);
      setSaveError(t('notificationTemplates.error'));
    }
  };

  const handleReset = async (templateKey: string) => {
    setSuccess(null);
    setSaveError(null);
    setResettingKey(templateKey);
    try {
      const draft = drafts[templateKey];
      const updated = await updateTemplates.mutateAsync([
        {
          templateKey: templateKey as NotificationTemplate['templateKey'],
          restoreToDefault: true,
          enabled: draft?.enabled,
        },
      ]);
      syncDrafts(updated);
      setSuccess(t('notificationTemplates.resetSuccess'));
    } catch (error) {
      console.error('Failed to reset notification template:', error);
      setSaveError(t('notificationTemplates.resetError'));
    } finally {
      setResettingKey(null);
    }
  };

  const sortedTemplates = useMemo<NotificationTemplate[]>(
    () => [...templates].sort((a, b) => a.templateKey.localeCompare(b.templateKey)),
    [templates]
  );

  const anniversaryDraftTemplate =
    drafts[NotificationTemplateKey.WORK_ANNIVERSARY] ??
    (anniversaryTemplate
      ? { body: anniversaryTemplate.body, enabled: anniversaryTemplate.enabled }
      : undefined);

  const reviewReminderDraftTemplate =
    drafts[NotificationTemplateKey.ANNIVERSARY_REVIEW_REMINDER] ??
    (reviewReminderTemplate
      ? { body: reviewReminderTemplate.body, enabled: reviewReminderTemplate.enabled }
      : undefined);

  const milestoneCheckInDraftTemplate =
    drafts[NotificationTemplateKey.START_DATE_MILESTONE] ??
    (milestoneCheckInTemplate
      ? { body: milestoneCheckInTemplate.body, enabled: milestoneCheckInTemplate.enabled }
      : undefined);

  const quarterlyEvalReminderDraftTemplate =
    drafts[NotificationTemplateKey.QUARTERLY_EVAL_REMINDER] ??
    (quarterlyEvalReminderTemplate
      ? {
          body: quarterlyEvalReminderTemplate.body,
          enabled: quarterlyEvalReminderTemplate.enabled,
        }
      : undefined);

  // Don't render admin content (or fire the admin-only query) for non-admins;
  // the effect above redirects them to the dashboard.
  if (adminLoading || !isAdmin) {
    return null;
  }

  return (
    <div className="container mx-auto py-8 px-4 max-w-4xl" data-testid="notification-templates-page">
      <Button
        variant="ghost"
        size="sm"
        className="mb-4"
        onClick={() => navigate('/settings')}
        data-testid="notification-templates-back"
      >
        <ChevronLeft className="h-4 w-4 mr-1" />
        {t('notificationTemplates.back')}
      </Button>

      <PageHeader
        title={t('notificationTemplates.title')}
        description={t('notificationTemplates.description')}
        testId="notification-templates-header"
      >
        <Button
          onClick={handleSave}
          disabled={isSaving || !hasChanges || hasErrors}
          data-testid="notification-templates-save"
        >
          <Save className="h-4 w-4 mr-2" />
          {isSaving
            ? t('notificationTemplates.saving')
            : t('notificationTemplates.save')}
        </Button>
      </PageHeader>

      {success && (
        <Alert className="mb-6 border-green-500 text-green-800" data-testid="notification-templates-success">
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}
      {saveError && (
        <Alert variant="destructive" className="mb-6" data-testid="notification-templates-save-error">
          <AlertDescription>{saveError}</AlertDescription>
        </Alert>
      )}

      {isError && (
        <Alert variant="destructive" className="mb-6" data-testid="notification-templates-load-error">
          <AlertDescription>{t('notificationTemplates.loadError')}</AlertDescription>
        </Alert>
      )}

      {isLoading ? (
        <div className="space-y-6">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      ) : (
        <div className="space-y-6">
          {anniversaryDraft && anniversaryTemplate && anniversaryDraftTemplate && (
            <WorkAnniversaryCard
              draft={anniversaryDraft}
              body={anniversaryDraftTemplate.body}
              enabled={anniversaryDraftTemplate.enabled}
              allowedParams={anniversaryAllowedParams}
              milestoneErrors={milestoneErrors}
              isResetting={resettingKey === NotificationTemplateKey.WORK_ANNIVERSARY}
              onEnabledChange={(value) =>
                updateDraft(NotificationTemplateKey.WORK_ANNIVERSARY, { enabled: value })
              }
              onChange={updateAnniversaryDraft}
              onReset={() => handleReset(NotificationTemplateKey.WORK_ANNIVERSARY)}
            />
          )}
          {reviewReminderDraft &&
            reviewReminderTemplate &&
            reviewReminderDraftTemplate && (
              <PerformanceReviewReminderCard
                draft={reviewReminderDraft}
                body={reviewReminderDraftTemplate.body}
                enabled={reviewReminderDraftTemplate.enabled}
                allowedParams={reviewReminderAllowedParams}
                bodyError={
                  validationErrors[NotificationTemplateKey.ANNIVERSARY_REVIEW_REMINDER]
                }
                isResetting={
                  resettingKey === NotificationTemplateKey.ANNIVERSARY_REVIEW_REMINDER
                }
                onEnabledChange={(value) =>
                  updateDraft(NotificationTemplateKey.ANNIVERSARY_REVIEW_REMINDER, {
                    enabled: value,
                  })
                }
                onChange={updateReviewReminderDraft}
                onBodyChange={(value) =>
                  updateDraft(NotificationTemplateKey.ANNIVERSARY_REVIEW_REMINDER, {
                    body: value,
                  })
                }
                onReset={() =>
                  handleReset(NotificationTemplateKey.ANNIVERSARY_REVIEW_REMINDER)
                }
              />
            )}
          {milestoneCheckInDraft &&
            milestoneCheckInTemplate &&
            milestoneCheckInDraftTemplate && (
              <StartDateMilestoneCard
                draft={milestoneCheckInDraft}
                body={milestoneCheckInDraftTemplate.body}
                enabled={milestoneCheckInDraftTemplate.enabled}
                allowedParams={milestoneCheckInAllowedParams}
                bodyError={validationErrors[NotificationTemplateKey.START_DATE_MILESTONE]}
                isResetting={resettingKey === NotificationTemplateKey.START_DATE_MILESTONE}
                onEnabledChange={(value) =>
                  updateDraft(NotificationTemplateKey.START_DATE_MILESTONE, { enabled: value })
                }
                onChange={updateMilestoneCheckInDraft}
                onBodyChange={(value) =>
                  updateDraft(NotificationTemplateKey.START_DATE_MILESTONE, { body: value })
                }
                onReset={() => handleReset(NotificationTemplateKey.START_DATE_MILESTONE)}
              />
            )}
          {quarterlyEvalReminderDraft &&
            quarterlyEvalReminderTemplate &&
            quarterlyEvalReminderDraftTemplate && (
              <QuarterlyEvalReminderCard
                draft={quarterlyEvalReminderDraft}
                body={quarterlyEvalReminderDraftTemplate.body}
                enabled={quarterlyEvalReminderDraftTemplate.enabled}
                allowedParams={quarterlyEvalReminderAllowedParams}
                bodyError={validationErrors[NotificationTemplateKey.QUARTERLY_EVAL_REMINDER]}
                isResetting={resettingKey === NotificationTemplateKey.QUARTERLY_EVAL_REMINDER}
                onEnabledChange={(value) =>
                  updateDraft(NotificationTemplateKey.QUARTERLY_EVAL_REMINDER, { enabled: value })
                }
                onChange={updateQuarterlyEvalReminderDraft}
                onBodyChange={(value) =>
                  updateDraft(NotificationTemplateKey.QUARTERLY_EVAL_REMINDER, { body: value })
                }
                onReset={() => handleReset(NotificationTemplateKey.QUARTERLY_EVAL_REMINDER)}
              />
            )}
          {sortedTemplates
            .filter(
              (tpl) =>
                tpl.templateKey !== NotificationTemplateKey.WORK_ANNIVERSARY &&
                tpl.templateKey !== NotificationTemplateKey.ANNIVERSARY_REVIEW_REMINDER &&
                tpl.templateKey !== NotificationTemplateKey.START_DATE_MILESTONE &&
                tpl.templateKey !== NotificationTemplateKey.QUARTERLY_EVAL_REMINDER
            )
            .map((tpl) => {
            const draft = drafts[tpl.templateKey] ?? { body: tpl.body, enabled: tpl.enabled };
            return (
              <NotificationTemplateEditor
                key={tpl.id}
                templateKey={tpl.templateKey}
                body={draft.body}
                enabled={draft.enabled}
                allowedParams={tpl.allowedParams}
                error={validationErrors[tpl.templateKey]}
                isResetting={resettingKey === tpl.templateKey}
                onBodyChange={(value) => updateDraft(tpl.templateKey, { body: value })}
                onEnabledChange={(value) => updateDraft(tpl.templateKey, { enabled: value })}
                onReset={() => handleReset(tpl.templateKey)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default NotificationTemplates;
